<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
<HrTopbar 
  :page-title="currentPageTitle"
  :is-sidebar-open="isSidebarOpen"
  :is-dark-mode="isDarkMode"
  @toggle-sidebar="toggleSidebar"
  @toggle-dark-mode="toggleDarkMode"
  @view-leave-request="handleTopbarLeaveRequest" 
/>
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- ============================================ -->
        <!-- DASHBOARD VIEW -->
        <!-- ============================================ -->
        <template v-if="currentView === 'dashboard'">
          
          <!-- Page Header -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Leave Management</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Track and manage employee leave requests</p>
            </div>
            <button 
              @click="openNewLeaveModal" 
              class="inline-flex items-center justify-center px-5 py-2.5 bg-[#76ba51] hover:bg-[#65a348] text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              New Leave Request
            </button>
          </div>

          <!-- Summary Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            
            <!-- ✅ CLICKABLE: Total Staff on Leave -->
            <div 
              @click="currentView = 'staff-on-leave'"
              class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all cursor-pointer group"
            >
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Staff on Leave</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{{ totalStaffOnLeave }}</p>
                </div>
                <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div class="flex items-center justify-between mt-4">
                <p class="text-xs text-gray-500 dark:text-gray-400">Click to view details</p>
                <svg class="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>

            <!-- Teaching Staff on Leave -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Teaching Staff on Leave</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ teachingStaffOnLeave }}</p>
                  <div class="flex flex-wrap gap-2 mt-3">
                    <span class="text-xs font-medium bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-md">N: {{ staffOnLeaveBySection.nursery }}</span>
                    <span class="text-xs font-medium bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 px-2 py-1 rounded-md">P: {{ staffOnLeaveBySection.primary }}</span>
                    <span class="text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-md">S: {{ staffOnLeaveBySection.secondary }}</span>
                  </div>
                </div>
                <div class="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">N=Nursery, P=Primary, S=Secondary</p>
            </div>

            <!-- Non-Teaching Staff on Leave -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Non-Teaching on Leave</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ nonTeachingStaffOnLeave }}</p>
                </div>
                <div class="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Administrative and support staff</p>
            </div>

            <!-- Pending Requests -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Requests</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ pendingLeaveRequests }}</p>
                </div>
                <div class="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Awaiting your approval</p>
            </div>
          </div>

          <!-- Recent People on Leave -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-8">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Recent People on Leave</h3>
              <button @click="recentFilter = 'all'" class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                View All
              </button>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employee</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Leave Type</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Approver</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="leave in recentLeaves" :key="leave.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {{ getInitials(leave.employeeName) }}
                        </div>
                        <div class="ml-3">
                          <div class="text-sm font-medium text-gray-900 dark:text-white">{{ leave.employeeName }}</div>
                          <div class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ leave.section }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(leave.type) }">
                        {{ leave.type }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {{ formatDate(leave.startDate) }} - {{ formatDate(leave.endDate) }}
                      <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ leave.duration }} days</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(leave.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">
                        {{ leave.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {{ leave.approver || '—' }}
                    </td>
                  </tr>
                  <tr v-if="recentLeaves.length === 0">
                    <td colspan="5" class="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No recent leave records found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Employee Search & Management -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Find Employee</h3>
            
            <div class="relative mb-6">
              <input 
                v-model="employeeSearch" 
                type="text" 
                placeholder="Search by Staff ID or Name (min 2 characters)..." 
                class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent transition"
              >
              <svg class="absolute left-3 top-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              
              <ul v-if="employeeSearch.trim().length >= 2" class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                <template v-if="searchResults.length > 0">
                  <li 
                    v-for="employee in searchResults" 
                    :key="employee.id" 
                    @click="selectEmployee(employee)"
                    class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-900 dark:text-white">{{ employee.name }}</span>
                      <span class="text-gray-500 dark:text-gray-400 text-xs">{{ employee.position }}</span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">ID: {{ employee.employeeCode }} • {{ employee.department }}</div>
                  </li>
                </template>
                <li v-else class="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  No employees found matching "{{ employeeSearch }}"
                </li>
              </ul>
            </div>

            <!-- Employee Details & Leave Management -->
            <div v-if="selectedEmployee" class="space-y-8 animate-fade-in">
              
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employee Name</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.name }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Position</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.position }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Department</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white capitalize">{{ selectedEmployee.department }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Staff ID</p>
                  <p class="mt-1 font-mono font-medium text-gray-900 dark:text-white">{{ selectedEmployee.employeeCode }}</p>
                </div>
              </div>

              <div>
                <h4 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Leave Balance</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div v-for="(balance, type) in leaveBalances" :key="type" class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <div class="flex justify-between items-start">
                      <div>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ type }}</p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">{{ balance.remaining }}<span class="text-sm text-gray-400 font-normal">/{{ balance.total }}</span></p>
                      </div>
                      <span class="px-2 py-1 text-xs rounded-full font-medium" 
                            :class="balance.remaining > 0 ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'">
                        {{ balance.remaining > 0 ? 'Available' : 'Exhausted' }}
                      </span>
                    </div>
                    <div class="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      Last taken: {{ balance.lastTaken || 'Never' }}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
                  <h4 class="text-md font-semibold text-gray-900 dark:text-white">Leave History</h4>
                  <div class="flex space-x-3">
                    <select v-model="leaveStatusFilter" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                      <option value="all">All Statuses</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <select v-model="leaveTypeFilter" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                      <option value="all">All Types</option>
                      <option v-for="type in leaveTypes" :key="type.id" :value="type.name">{{ type.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Days</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Approver</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr v-for="request in filteredLeaveHistory" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(request.type) }">
                            {{ request.type }}
                          </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {{ formatDate(request.startDate) }} - {{ formatDate(request.endDate) }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {{ request.duration }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span :class="getStatusClass(request.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">
                            {{ request.status }}
                          </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {{ request.approver || 'N/A' }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button @click="viewLeaveDetails(request)" class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 transition-colors">
                            View
                          </button>
                          <button v-if="request.status === 'pending'" @click="cancelLeaveRequest(request.id)" class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors">
                            Cancel
                          </button>
                        </td>
                      </tr>
                      <tr v-if="filteredLeaveHistory.length === 0">
                        <td colspan="6" class="px-6 py-12 text-center">
                          <p class="text-sm text-gray-500 dark:text-gray-400">No leave requests found for this employee.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ============================================ -->
        <!-- STAFF ON LEAVE DETAILED VIEW -->
        <!-- ============================================ -->
        <template v-else-if="currentView === 'staff-on-leave'">
          
          <!-- Back Button + Header -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <button 
                @click="currentView = 'dashboard'"
                class="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Staff Currently on Leave</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Showing <span class="font-semibold text-gray-900 dark:text-white">{{ filteredStaffOnLeave.length }}</span> of {{ activeLeaves.length }} staff on leave today
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Today: {{ formatDate(today) }}</span>
            </div>
          </div>

          <!-- Filters Bar -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              
              <!-- Search by name -->
              <div class="relative lg:col-span-2">
                <svg class="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input 
                  v-model="staffOnLeaveFilters.search"
                  type="text"
                  placeholder="Search by name or staff ID..."
                  class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#035a76]"
                >
              </div>

              <!-- Department Filter -->
              <select v-model="staffOnLeaveFilters.department" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76]">
                <option value="all">All Departments</option>
                <option v-for="dept in availableDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
              </select>

              <!-- Section Filter -->
              <select v-model="staffOnLeaveFilters.section" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76]">
                <option value="all">All Sections</option>
                <option v-for="sec in availableSections" :key="sec.id" :value="sec.id">{{ sec.name }}</option>
              </select>

              <!-- Position Filter -->
              <select v-model="staffOnLeaveFilters.position" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76]">
                <option value="all">All Positions</option>
                <option v-for="pos in availablePositions" :key="pos.id" :value="pos.id">{{ pos.title }}</option>
              </select>
            </div>

            <!-- Active Filters / Reset -->
            <div v-if="hasActiveFilters" class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <div class="flex flex-wrap gap-2">
                <span v-if="staffOnLeaveFilters.department !== 'all'" class="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                  Dept: {{ getDepartmentName(staffOnLeaveFilters.department) }}
                </span>
                <span v-if="staffOnLeaveFilters.section !== 'all'" class="inline-flex items-center px-2 py-1 rounded-md text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                  Section: {{ getSectionName(staffOnLeaveFilters.section) }}
                </span>
                <span v-if="staffOnLeaveFilters.position !== 'all'" class="inline-flex items-center px-2 py-1 rounded-md text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                  Position: {{ getPositionName(staffOnLeaveFilters.position) }}
                </span>
              </div>
              <button @click="resetFilters" class="text-xs text-red-600 dark:text-red-400 hover:underline font-medium">
                Clear all filters
              </button>
            </div>
          </div>

          <!-- Breakdown Stats by Filter -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-xs text-gray-500 dark:text-gray-400 uppercase">Total</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">{{ filteredStaffOnLeave.length }}</p>
            </div>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-xs text-indigo-600 dark:text-indigo-400 uppercase">Teachers</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {{ filteredStaffOnLeave.filter(s => s.userType === 'Teacher').length }}
              </p>
            </div>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-xs text-emerald-600 dark:text-emerald-400 uppercase">Non-Teaching</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {{ filteredStaffOnLeave.filter(s => s.userType !== 'Teacher').length }}
              </p>
            </div>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-xs text-amber-600 dark:text-amber-400 uppercase">Avg. Duration</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {{ averageLeaveDuration }} days
              </p>
            </div>
          </div>

          <!-- Staff on Leave Table -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employee</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Department</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Section</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Leave Type</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Days Left</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="staff in filteredStaffOnLeave" :key="staff.leaveId" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {{ getInitials(staff.employeeName) }}
                        </div>
                        <div class="ml-3">
                          <div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.employeeName }}</div>
                          <div class="text-xs text-gray-500 dark:text-gray-400 font-mono">{{ staff.employeeCode }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {{ staff.position }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {{ staff.department }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span v-if="staff.section && staff.section !== 'N/A'" 
                            class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                        {{ staff.section }}
                      </span>
                      <span v-else class="text-xs text-gray-400">—</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(staff.type) }">
                        {{ staff.type }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {{ formatDate(staff.startDate) }} – {{ formatDate(staff.endDate) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                        {{ staff.daysRemaining }} day{{ staff.daysRemaining !== 1 ? 's' : '' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button @click="viewLeaveDetails(staff)" class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 transition-colors">
                        View Details
                      </button>
                    </td>
                  </tr>
                  <tr v-if="filteredStaffOnLeave.length === 0">
                    <td colspan="8" class="px-6 py-16 text-center">
                      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No staff on leave match the current filters.</p>
                      <button v-if="hasActiveFilters" @click="resetFilters" class="mt-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                        Clear filters
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

      </main>
    </div>

    <!-- New Leave Modal (unchanged) -->
    <Transition name="modal">
      <div v-if="showNewLeaveModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showNewLeaveModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">New Leave Request</h3>
            <button @click="showNewLeaveModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-6 space-y-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Employee *</label>
              <Multiselect
                v-model="selectedEmployeeModel"
                :options="mappedEmployees"
                :custom-label="app => `${app.name} (${app.employeeCode})`"
                track-by="id"
                searchable
                placeholder="Search employee by name or ID..."
                :show-labels="false"
                class="multiselect-custom"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Leave Type *</label>
              <select v-model="newLeave.leave_type_id" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]">
                <option value="">Select Leave Type</option>
                <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Date Range *</label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input v-model="newLeave.startDate" type="date" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]">
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input v-model="newLeave.endDate" type="date" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]">
                </div>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason *</label>
              <textarea v-model="newLeave.reason" rows="3" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] resize-none" placeholder="Enter reason for leave..."></textarea>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50">
            <button @click="showNewLeaveModal = false" type="button" class="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
              Cancel
            </button>
            <button @click="submitLeaveRequest" type="button" :disabled="isSubmitting" class="px-5 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-[#76ba51] hover:bg-[#65a348] transition-colors shadow-sm disabled:opacity-50">
              {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Leave Details Modal (unchanged) -->
    <Transition name="modal">
      <div v-if="selectedLeave" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="selectedLeave = null">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Leave Details - {{ selectedLeave.type }}</h3>
            <button @click="selectedLeave = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-4">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employee</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedLeave.employeeName }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dates</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedLeave.startDate) }} to {{ formatDate(selectedLeave.endDate) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Duration</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedLeave.duration }} days</p>
                </div>
              </div>
              <div class="space-y-4">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</p>
                  <p class="mt-1">
                    <span :class="getStatusClass(selectedLeave.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">
                      {{ selectedLeave.status }}
                    </span>
                  </p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Submitted On</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDateTime(selectedLeave.submittedOn) }}</p>
                </div>
                <div v-if="selectedLeave.approver">
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Approved By</p>
                  <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedLeave.approver }}</p>
                </div>
              </div>
            </div>
            <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reason</p>
              <p class="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-700 dark:text-gray-300">{{ selectedLeave.reason }}</p>
            </div>
          </div>
          <div v-if="selectedLeave.status === 'pending'" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50">
            <button @click="updateLeaveStatus(selectedLeave.id, 'rejected')" class="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-red-600 hover:bg-red-700 transition-colors">
              Reject
            </button>
            <button @click="updateLeaveStatus(selectedLeave.id, 'approved')" class="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 transition-colors">
              Approve
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Multiselect from 'vue-multiselect';
import api from '@/services/api';
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';

// 1. Router Initialization
const route = useRoute();
const router = useRouter();

// 2. Core State
const isSidebarOpen = ref(false);
const isDarkMode = ref(false);
const currentPageTitle = ref('Leave Management');
const currentView = ref('dashboard'); // ✅ Added missing variable

// 3. API Data State
const leaveTypes = ref([]);
const employees = ref([]);
const allLeaves = ref([]);

// 4. UI State & Filters
const employeeSearch = ref('');
const searchResults = ref([]);
const selectedEmployee = ref(null);
const leaveStatusFilter = ref('all');
const leaveTypeFilter = ref('all');
const recentFilter = ref('all');
const showNewLeaveModal = ref(false);
const selectedLeave = ref(null);
const isSubmitting = ref(false);
const selectedEmployeeModel = ref(null);

const staffOnLeaveFilters = ref({
  search: '',
  department: 'all',
  section: 'all',
  position: 'all'
});

const newLeave = ref({
  applicant_id: '',
  leave_type_id: '',
  startDate: '',
  endDate: '',
  reason: ''
});

// 5. Data Fetching
const fetchData = async () => {
  try {
    const [typesRes, empRes, leavesRes] = await Promise.all([
      api.get('LeaveTypes.php'),
      api.get('get_hired_applicants.php'),
      api.get('Leaves.php')
    ]);

    leaveTypes.value = typesRes.data || [];
    employees.value = empRes.data?.data || [];
    allLeaves.value = leavesRes.data?.data || [];
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

// 6. Mapping Functions
const mapEmployee = (emp) => ({
  id: String(emp.applicant_id),
  name: emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
  position: emp.position?.position_title || 'N/A',
  positionId: emp.position?.position_id || null,
  department: emp.department?.department_name || 'N/A',
  departmentId: emp.department?.department_id || null,
  section: emp.section?.section_name || 'N/A',
  sectionId: emp.section?.section_id || null,
  userType: emp.user_type || 'N/A',
  employeeCode: emp.employee_code || 'N/A',
  raw: emp
});

const mappedEmployees = computed(() => employees.value.map(mapEmployee));

const enrichedLeaves = computed(() => {
  return allLeaves.value.map(leave => {
    const emp = employees.value.find(e => String(e.applicant_id) === String(leave.applicant_id));
    const mapped = emp ? mapEmployee(emp) : null;
    return {
      id: leave.id,
      leaveId: leave.id,
      applicant_id: String(leave.applicant_id),
      employeeName: mapped?.name || leave.employee_name || 'Unknown',
      employeeCode: mapped?.employeeCode || 'N/A',
      position: mapped?.position || 'N/A',
      positionId: mapped?.positionId,
      department: mapped?.department || 'N/A',
      departmentId: mapped?.departmentId,
      section: mapped?.section || 'N/A',
      sectionId: mapped?.sectionId,
      userType: mapped?.userType || 'Unknown',
      type: leave.leave_type_name,
      startDate: leave.start_date,
      endDate: leave.end_date,
      duration: calculateDays(leave.start_date, leave.end_date),
      status: leave.status,
      reason: leave.reason,
      approver: leave.approved_by,
      submittedOn: leave.created_at
    };
  });
});

// 7. Watchers
watch(employeeSearch, (newVal) => {
  const query = newVal.trim().toLowerCase();
  if (query.length < 2) { 
    searchResults.value = []; 
    return; 
  }
  searchResults.value = mappedEmployees.value.filter(emp => {
    const name = emp.name ? emp.name.toLowerCase() : '';
    const code = emp.employeeCode ? emp.employeeCode.toLowerCase() : '';
    return name.includes(query) || code.includes(query);
  }).slice(0, 10);
});

// ✅ Notification Redirect Watcher (Placed AFTER dependencies are defined)
watch(
  () => route.query.viewLeave,
  (leaveId) => {
    if (leaveId) {
      currentView.value = 'dashboard';
      
      const fullLeave = enrichedLeaves.value.find(l => String(l.id) === String(leaveId));
      
      if (fullLeave) {
        selectedLeave.value = fullLeave;
      } else {
        fetchData().then(() => {
          const updatedLeave = enrichedLeaves.value.find(l => String(l.id) === String(leaveId));
          if (updatedLeave) {
            selectedLeave.value = updatedLeave;
          } else {
            console.warn('Leave request not found in enriched data.');
          }
        });
      }
      
      // Clear the query param so it doesn't reopen on refresh
      router.replace({ 
        path: route.path, 
        query: { ...route.query, viewLeave: undefined } 
      });
    }
  },
  { immediate: true }
);

// 8. Stats & Computed
const today = new Date().toISOString().split('T')[0];

const activeLeaves = computed(() => enrichedLeaves.value.filter(l => 
  l.status === 'approved' && l.startDate <= today && l.endDate >= today
));

const totalStaffOnLeave = computed(() => activeLeaves.value.length);
const teachingStaffOnLeave = computed(() => activeLeaves.value.filter(l => l.userType === 'Teacher').length);
const nonTeachingStaffOnLeave = computed(() => activeLeaves.value.filter(l => l.userType !== 'Teacher').length);
const pendingLeaveRequests = computed(() => enrichedLeaves.value.filter(l => l.status === 'pending').length);

const staffOnLeaveBySection = computed(() => {
  const counts = { nursery: 0, primary: 0, secondary: 0 };
  activeLeaves.value.filter(l => l.userType === 'Teacher').forEach(l => {
    const sec = (l.section || '').toLowerCase();
    if (sec.includes('nursery') || sec.includes('early')) counts.nursery++;
    else if (sec.includes('primary')) counts.primary++;
    else if (sec.includes('secondary') || sec.includes('high')) counts.secondary++;
  });
  return counts;
});

const recentLeaves = computed(() => {
  return [...enrichedLeaves.value]
    .sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn))
    .slice(0, 5);
});

const availableDepartments = computed(() => {
  const map = new Map();
  activeLeaves.value.forEach(l => {
    if (l.departmentId && l.department && l.department !== 'N/A') {
      map.set(String(l.departmentId), { id: l.departmentId, name: l.department });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
});

const availableSections = computed(() => {
  const map = new Map();
  activeLeaves.value.forEach(l => {
    if (l.sectionId && l.section && l.section !== 'N/A') {
      map.set(String(l.sectionId), { id: l.sectionId, name: l.section });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
});

const availablePositions = computed(() => {
  const map = new Map();
  activeLeaves.value.forEach(l => {
    if (l.positionId && l.position && l.position !== 'N/A') {
      map.set(String(l.positionId), { id: l.positionId, title: l.position });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
});

const filteredStaffOnLeave = computed(() => {
  const f = staffOnLeaveFilters.value;
  const search = f.search.trim().toLowerCase();
  
  return activeLeaves.value
    .filter(l => {
      if (f.department !== 'all' && String(l.departmentId) !== String(f.department)) return false;
      if (f.section !== 'all' && String(l.sectionId) !== String(f.section)) return false;
      if (f.position !== 'all' && String(l.positionId) !== String(f.position)) return false;
      if (search) {
        const name = (l.employeeName || '').toLowerCase();
        const code = (l.employeeCode || '').toLowerCase();
        if (!name.includes(search) && !code.includes(search)) return false;
      }
      return true;
    })
    .map(l => ({
      ...l,
      daysRemaining: calculateDaysRemaining(l.endDate)
    }))
    .sort((a, b) => a.daysRemaining - b.daysRemaining);
});

const hasActiveFilters = computed(() => {
  const f = staffOnLeaveFilters.value;
  return f.department !== 'all' || f.section !== 'all' || f.position !== 'all' || f.search.trim() !== '';
});

const averageLeaveDuration = computed(() => {
  if (filteredStaffOnLeave.value.length === 0) return 0;
  const total = filteredStaffOnLeave.value.reduce((sum, s) => sum + s.duration, 0);
  return (total / filteredStaffOnLeave.value.length).toFixed(1);
});

const leaveBalances = computed(() => {
  if (!selectedEmployee.value) return {};
  const balances = {};
  const defaultBalances = { total: 9, remaining: 8, lastTaken: 'N/A' };
  if (leaveTypes.value.length > 0) {
    leaveTypes.value.forEach(type => { balances[type.name] = { ...defaultBalances }; });
  } else {
    balances['Annual'] = { ...defaultBalances };
    balances['Sick'] = { ...defaultBalances };
  }
  return balances;
});

const filteredLeaveHistory = computed(() => {
  if (!selectedEmployee.value) return [];
  let filtered = enrichedLeaves.value.filter(leave => String(leave.applicant_id) === String(selectedEmployee.value.id));
  if (leaveStatusFilter.value !== 'all') {
    filtered = filtered.filter(leave => leave.status === leaveStatusFilter.value);
  }
  if (leaveTypeFilter.value !== 'all') {
    filtered = filtered.filter(leave => leave.type && leave.type.toLowerCase() === leaveTypeFilter.value.toLowerCase());
  }
  return filtered.sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));
});

// 9. Methods
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };
  return map[status] || 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const getLeaveTypeColor = (type) => {
  if (!type) return '#9E9E9E';
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = type.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const calculateDays = (start, end) => {
  if (!start || !end) return 0;
  const diffTime = Math.abs(new Date(end) - new Date(start));
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getDepartmentName = (id) => availableDepartments.value.find(d => String(d.id) === String(id))?.name || '';
const getSectionName = (id) => availableSections.value.find(s => String(s.id) === String(id))?.name || '';
const getPositionName = (id) => availablePositions.value.find(p => String(p.id) === String(id))?.title || '';

const resetFilters = () => {
  staffOnLeaveFilters.value = { search: '', department: 'all', section: 'all', position: 'all' };
};

const calculateDaysRemaining = (endDate) => {
  if (!endDate) return 0;
  const diff = Math.abs(new Date(endDate) - new Date(today));
  return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

const selectEmployee = (employee) => {
  selectedEmployee.value = employee;
  employeeSearch.value = employee.name;
  searchResults.value = [];
};

const openNewLeaveModal = () => {
  selectedEmployeeModel.value = null;
  newLeave.value = {
    applicant_id: '',
    leave_type_id: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: ''
  };
  showNewLeaveModal.value = true;
};

// const submitLeaveRequest = async () => {
//   if (!selectedEmployeeModel.value || !newLeave.value.leave_type_id || !newLeave.value.startDate || !newLeave.value.endDate) {
//     alert('Please fill in all required fields.');
//     return;
//   }
//   isSubmitting.value = true;
//   try {
//     const response = await api.post('Leaves.php', {
//       applicant_id: selectedEmployeeModel.value.id,
//       leave_type_id: newLeave.value.leave_type_id,
//       start_date: newLeave.value.startDate,
//       end_date: newLeave.value.endDate,
//       reason: newLeave.value.reason
//     });
//     if (response.data.success) {
//       alert('Leave request submitted successfully!');
//       showNewLeaveModal.value = false;
//       fetchData();
//     } else {
//       alert(response.data.message || 'Failed to submit request.');
//     }
//   } catch (error) {
//     console.error('Submit error:', error);
//     alert('An error occurred while submitting the request.');
//   } finally {
//     isSubmitting.value = false;
//   }
// };

const submitLeaveRequest = async () => {
  console.log('--- 🚀 SUBMIT DEBUG START ---');
  console.log('1. Selected Employee Model:', selectedEmployeeModel.value);
  console.log('2. New Leave Data:', newLeave.value);

  // 1. STRICT VALIDATION: Ensure an employee is actually selected
  if (!selectedEmployeeModel.value) {
    alert('⚠️ Please select an employee from the dropdown.');
    return;
  }

  // 2. SAFEGUARD: Warn if the selected person is an Admin (prevent accidental self-submission)
  if (selectedEmployeeModel.value.userType === 'Admin' || selectedEmployeeModel.value.name === 'Main Admin') {
    const confirmAdmin = confirm('⚠️ You have selected the Admin account. Are you sure you want to submit this leave request for the Admin, rather than an employee?');
    if (!confirmAdmin) return;
  }

  if (!newLeave.value.leave_type_id || !newLeave.value.startDate || !newLeave.value.endDate || !newLeave.value.reason) {
    alert('Please fill in all required fields.');
    return;
  }

  isSubmitting.value = true;
  try {
    // 3. EXPLICITLY build the payload using ONLY the selected model's ID
    const payload = {
      applicant_id: selectedEmployeeModel.value.id, // This will be exactly what is in the dropdown
      leave_type_id: newLeave.value.leave_type_id,
      start_date: newLeave.value.startDate,
      end_date: newLeave.value.endDate,
      reason: newLeave.value.reason
    };

    console.log('✅ FINAL PAYLOAD BEING SENT TO API:', payload);
    console.log('--- 🚀 SUBMIT DEBUG END ---');

    const response = await api.post('Leaves.php', payload);

    if (response.data.success) {
      alert('Leave request submitted successfully!');
      showNewLeaveModal.value = false;
      fetchData(); // Refresh the table
    } else {
      alert(response.data.message || 'Failed to submit request.');
    }
  } catch (error) {
    console.error('❌ Submit error:', error);
    alert('An error occurred while submitting the request.');
  } finally {
    isSubmitting.value = false;
  }
};
const viewLeaveDetails = (leave) => { selectedLeave.value = leave; };

const updateLeaveStatus = async (id, status) => {
  if (!confirm(`Are you sure you want to ${status} this request?`)) return;
  try {
    const response = await api.post('Leaves.php', {
      action: 'update_status',
      id: id,
      status: status,
      approved_by: 'HR Admin'
    });
    if (response.data.success) {
      alert(`Request ${status} successfully!`);
      selectedLeave.value = null;
      fetchData();
    } else {
      alert(response.data.message || 'Failed to update status.');
    }
  } catch (error) {
    console.error('Update error:', error);
    alert('An error occurred while updating the request.');
  }
};

const cancelLeaveRequest = async (id) => {
  if (!confirm('Are you sure you want to cancel this leave request?')) return;
  try {
    const response = await api.post('Leaves.php', {
      action: 'update_status',
      id: id,
      status: 'cancelled',
      approved_by: 'System'
    });
    if (response.data.success) {
      alert('Leave request cancelled.');
      fetchData();
    }
  } catch (error) {
    console.error('Cancel error:', error);
  }
};

// 10. Lifecycle
onMounted(() => {
  fetchData();
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
.multiselect-custom .multiselect__tags {
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  min-height: 42px;
  padding: 0.5rem 0.75rem;
}
.dark .multiselect-custom .multiselect__tags {
  background-color: #374151;
  border-color: #4b5563;
  color: white;
}
.multiselect-custom .multiselect__input,
.multiselect-custom .multiselect__single {
  background: transparent;
  color: #111827;
  margin-top: 2px;
}
.dark .multiselect-custom .multiselect__input,
.dark .multiselect-custom .multiselect__single {
  color: white;
}
.multiselect-custom .multiselect__placeholder { color: #9ca3af; }
.multiselect-custom .multiselect__content-wrapper {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}
.dark .multiselect-custom .multiselect__content-wrapper {
  background-color: #1f2937;
  border-color: #374151;
}
.multiselect-custom .multiselect__element span { color: #111827; }
.dark .multiselect-custom .multiselect__element span { color: white; }
.multiselect-custom .multiselect__option--highlight {
  background: #035a76 !important;
  color: white !important;
}
.multiselect-custom .multiselect__option--selected {
  background: rgba(3, 90, 118, 0.1);
  color: #035a76;
  font-weight: 600;
}
.dark .multiselect-custom .multiselect__option--selected {
  background: rgba(3, 90, 118, 0.2);
  color: #60a5fa;
}
</style>

<style scoped>
.animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.95); }
</style>